import React, { useEffect } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { ICustomerFormSchema } from 'src/generated-types/form-schema'
import { IMainComponentTypeFormSchema } from '../../generated-types/main-component-type'

export const useChecklistFormActions = (
  form: UseFormReturn<any>,
  checklistSchemas: ICustomerFormSchema[] | undefined,
) => {
  const [selectedChecklistIds, setSelectedChecklistIds] = React.useState<number[]>(
    (form.getValues('mainComponentTypeFormSchemas') as IMainComponentTypeFormSchema[] | undefined)?.map(
      m => m.formSchemaId,
    ) ?? [],
  )

  useEffect(() => {
    if (!checklistSchemas?.length) return

    const validChecklistIds = new Set(
      checklistSchemas.filter(s => s.id != null).map(s => s.id as number),
    )
    const currentMappings =
      (form.getValues('mainComponentTypeFormSchemas') as IMainComponentTypeFormSchema[] | undefined) ?? []

    const normalizedMappings = currentMappings
      .filter(mapping => validChecklistIds.has(mapping.formSchemaId))
      .map(mapping => ({
        formSchemaId: mapping.formSchemaId,
        movementTypes: Array.from(new Set(mapping.movementTypes ?? [])),
      }))

    if (JSON.stringify(normalizedMappings) !== JSON.stringify(currentMappings)) {
      form.setValue('mainComponentTypeFormSchemas', normalizedMappings)
    }

    const mappedIds = normalizedMappings.map(m => m.formSchemaId)
    if (JSON.stringify(mappedIds) !== JSON.stringify(selectedChecklistIds)) {
      setSelectedChecklistIds(mappedIds)
    }
  }, [checklistSchemas, form])

  const toggleChecklistMovement = React.useCallback(
    (formSchemaId: number, movementType: number, checked: boolean) => {
      const currentMappings =
        (form.getValues('mainComponentTypeFormSchemas') as IMainComponentTypeFormSchema[] | undefined) ?? []
      const currentEntry = currentMappings.find(mapping => mapping.formSchemaId === formSchemaId)
      const currentMovementTypes = currentEntry?.movementTypes ?? []
      const nextMovementTypes = checked
        ? Array.from(new Set([...currentMovementTypes, movementType]))
        : currentMovementTypes.filter(type => type !== movementType)

      const remainingMappings = currentMappings.filter(mapping => mapping.formSchemaId !== formSchemaId)
      const nextMappings =
        nextMovementTypes.length > 0
          ? [...remainingMappings, { formSchemaId, movementTypes: nextMovementTypes }]
          : remainingMappings

      form.setValue('mainComponentTypeFormSchemas', nextMappings, { shouldDirty: true })
    },
    [form],
  )

  const handleAddChecklist = (checklistId: number | null) => {
    if (checklistId && !selectedChecklistIds.includes(checklistId)) {
      setSelectedChecklistIds(prev => [...prev, checklistId])
      const currentMappings =
        (form.getValues('mainComponentTypeFormSchemas') as IMainComponentTypeFormSchema[] | undefined) ?? []
      form.setValue(
        'mainComponentTypeFormSchemas',
        [...currentMappings, { formSchemaId: checklistId, movementTypes: [] }],
        { shouldDirty: true },
      )
    }
  }

  const handleRemoveChecklist = (checklistId: number) => {
    setSelectedChecklistIds(prev => prev.filter(id => id !== checklistId))
    const currentMappings =
      (form.getValues('mainComponentTypeFormSchemas') as IMainComponentTypeFormSchema[] | undefined) ?? []
    form.setValue(
      'mainComponentTypeFormSchemas',
      currentMappings.filter(mapping => mapping.formSchemaId !== checklistId),
      { shouldDirty: true },
    )
  }

  const selectedChecklists = React.useMemo(
    () => checklistSchemas?.filter(s => s.id && selectedChecklistIds.includes(s.id as number)) ?? [],
    [checklistSchemas, selectedChecklistIds],
  )

  const availableChecklists = React.useMemo(
    () => checklistSchemas?.filter(s => s.id && !selectedChecklistIds.includes(s.id as number)) ?? [],
    [checklistSchemas, selectedChecklistIds],
  )

  return {
    selectedChecklists,
    availableChecklists,
    toggleChecklistMovement,
    handleAddChecklist,
    handleRemoveChecklist,
  }
}
